import React, { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import './AboutMe.css';

const skills = [
  { icon: 'fa-brands fa-python', name: 'Python' },
  { icon: 'fa-brands fa-golang', name: 'Go' },
  { icon: 'fa-brands fa-js', name: 'JavaScript' },
  { icon: 'fa-solid fa-brain', name: 'RAG' },
  { icon: 'fa-solid fa-robot', name: 'Vector DB' },
  { icon: 'fa-solid fa-network-wired', name: 'LangChain' },
  { icon: 'fa-solid fa-database', name: 'PostgreSQL' },
  { icon: 'fa-solid fa-database', name: 'Redis' },
  { icon: 'fa-brands fa-react', name: 'React' },
  { icon: 'fa-solid fa-dharmachakra', name: 'Kubernetes' },
  { icon: 'fa-brands fa-docker', name: 'Docker' },
  { icon: 'fa-brands fa-jenkins', name: 'Jenkins' },
];

function SectionHeading({ children, isInView }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <motion.h2
        className="section-title"
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
        style={{ position: 'relative', left: 'auto', transform: 'none', display: 'inline-block' }}
      >
        {children}
      </motion.h2>
      <motion.div
        className="section-title-underline"
        initial={{ scaleX: 0 }}
        animate={isInView ? { scaleX: 1 } : {}}
        transition={{ duration: 0.6, delay: 0.3, type: 'spring', stiffness: 200, damping: 20 }}
        style={{ transformOrigin: 'center' }}
      />
    </div>
  );
}

function AboutMe() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  useEffect(() => {
    const img = new Image();
    img.src = '/images/Profile_Hover.jpg';
  }, []);

  return (
    <section id="about" className="about" ref={ref} style={{ position: 'relative' }}>
      <div className="about__inner">
        <SectionHeading isInView={isInView}>./About_Me</SectionHeading>

        <div className="about__grid">
          <motion.div
            className="about__image-wrap"
            initial={{ opacity: 0, clipPath: 'inset(100% 0 0 0)' }}
            animate={isInView ? { opacity: 1, clipPath: 'inset(0% 0 0 0)' } : {}}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
          >
            <div className="about__image-glow" />
            <img
              src="/images/Profile.jpg"
              alt="Soumyajit Rout"
              className="about__image"
              onMouseEnter={(e) => (e.currentTarget.src = '/images/Profile_Hover.jpg')}
              onMouseLeave={(e) => (e.currentTarget.src = '/images/Profile.jpg')}
            />
          </motion.div>

          <motion.div
            className="about__text"
            style={{ position: 'relative', zIndex: 1 }}
            initial={{ opacity: 0, clipPath: 'inset(0 0 100% 0)' }}
            animate={isInView ? { opacity: 1, clipPath: 'inset(0 0 0% 0)' } : {}}
            transition={{ duration: 0.8, delay: 0.35, ease: [0.4, 0, 0.2, 1] }}
          >
            <p className="about__greeting">
              Hi There!{' '}
              <img
                src="https://media.giphy.com/media/hvRJCLFzcasrR4ia7z/giphy.gif"
                width="20"
                alt="wave"
                style={{ display: 'inline', verticalAlign: 'middle' }}
              />
            </p>

            <p>
              My name is Soumyajit, and I'm a Software Engineer passionate about building 
              scalable systems that sit at the intersection of backend engineering,
              distributed infrastructure, network automation, and AI-powered tooling.
              I primarily work with Python and Go, with experience spanning distributed systems, 
              telemetry platforms, automation frameworks, and Retrieval-Augmented Generation (RAG) applications.
            </p>

            <p>
              I enjoy solving complex engineering problems through thoughtful system design,
               reliability-focused architecture, and clean developer experiences.
                Whether it's designing automation platforms, building observability tooling,
                 or improving operational efficiency through intelligent systems,
                  I'm driven by the challenge of turning difficult problems into scalable and 
                  maintainable solutions.
            </p>
            <p>
              Beyond my core work, I enjoy building side projects that push me to explore new technologies 
              and sharpen my engineering depth. I'm naturally curious and constantly experimenting, learning, 
              and refining my understanding of systems, infrastructure, and software architecture.
            </p>

            <p>
              Outside of engineering, I enjoy long bike rides, exploring new places, and discovering experiences that fuel curiosity and creativity.
               I'm also deeply passionate about coffee brewing and love experimenting with methods like V60, AeroPress, and moka pot. 
               Always chasing the perfect cup through precision and iteration
               <img
                 src="/images/coffee-pot.gif"
                 alt="Coffee Pot Animation"
                 className="about__coffee-gif"
                 style={{
                   display: 'inline-block',
                   width: '40px',
                   height: '40px',
                   marginLeft: '10px',
                   marginBottom: '-8px',
                   verticalAlign: 'middle',
                   background: 'transparent',
                   boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)',
                   borderRadius: '8px',
                   mixBlendMode: 'multiply',
                   opacity: 0.92,
                 }}
               />.
            </p>

            <div className="about__skills-label">Technologies I work with</div>
            <ul className="about__skills">
              {skills.map((skill, i) => (
                <motion.li
                  key={skill.name}
                  className="about__skill"
                  initial={{ opacity: 0, y: 10 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.6 + i * 0.05 }}
                >
                  <i className={skill.icon} />
                  <span>{skill.name}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default AboutMe;
